import React, { Fragment, useEffect, useMemo, useState } from 'react';
import LoadingDataSimple from 'components/loading-data/simple';
import ListingCard, { ListingCardData } from 'components/listing-card/listing-card';
import ListingCardV2 from 'components/listing-card/listing-card-v2';
import ListingCardV3 from 'components/listing-card/listing-card-v3';
import styles from './style.module.scss';
import ConditionalWrapper from 'components/conditional-wrapper';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_CLICK_LISTING_CARD } from 'constants/events';
import { useRouter } from 'next/router';
import SaveSearchCard from 'components/listing-card/save-search-card';
import { useFeaturesContext } from 'contexts';
import LoadWhenVisible from 'components/load-when-visible';
import LoadingSkeleton from 'components/loading-skeleton';
import dynamic from 'next/dynamic';
import MapButtonCard from 'components/listing-card/map-card';
import { parseMilitaryPath } from 'data/military/militaryPathParser';
import { useListingCardClick } from 'hooks/use-listing-card-click';
import { useListingsCompliance } from 'hooks/use-listings-compliance';

import type { Breadcrumb } from 'components/breadcrumbs';
import type { ListingParams } from 'contexts/preferences/listing-params/types';
import type { OnListingCardClickProps } from 'utils/listing-card';
import LoadingText from 'components/loading-text';
import type { ListingCardDataWithCompliance } from 'utils/listing-card/listing-card-transformers';
import { PaginationMode } from 'components/control/pagination-nav';
import { PROPERTY_FAVOURITED_SOURCE_AREA_PAGE, PROPERTY_FAVOURITED_SOURCE_MAP_GRID } from '@bedrock/analytics';

// Proper dynamic imports at module level
const PaginationNav = dynamic(() => import('components/control/pagination-nav'), {
  loading: () => <LoadingText />,
  ssr: false,
});

const ListingsGridBreadcrumbs = dynamic(() => import('components/listings-grid/breadcrumbs'), {
  loading: () => <LoadingText />,
  ssr: false,
});

export type ListingsGridProps = {
  listings: readonly ListingCardData[];
  listingsWithCompliance?: readonly ListingCardDataWithCompliance[];
  isLoading: boolean;
  testId?: string;
  params: {
    page: {
      number: number;
      size: number;
    };
  };
  meta: {
    totalCount: number;
    totalPages: number;
  };
  breadcrumbs?: Breadcrumb[];
  setPageNumber?: (pageNumber: number) => void;
  onPrevPage?: () => void;
  onNextPage?: () => void;
  paginationMode?: PaginationMode;
  listingParams?: ListingParams;
  trackMapCardExperimentView?: () => void;
  trackMapCardClick?: () => void;
  showMapCardExperimentView?: boolean;
  onListingCardClick: (props: OnListingCardClickProps) => void;
  onListingCardClickWithCompliance?: ReturnType<typeof useListingCardClick>; // Click handler for v2 cards with analytics
  isOnSidePanel?: boolean;
  useListingCardWithCompliance: boolean; // Feature flag to enable v2 listing cards
  /** Area Page visual card (listing-card-v3). Takes precedence over legacy when true. */
  useListingCardV3?: boolean;
  /** Source for property_favourited analytics. Defaults to MAP_GRID when isOnSidePanel, else AREA_PAGE. */
  favouriteSource?: import('@bedrock/analytics').PropertyFavouritedSource;
  /** When set (e.g. area page with map pins), syncs list hover with map pin highlight. */
  onListingHover?: (listingId: number | null) => void;
};

export default function ListingsGrid({ listings, listingsWithCompliance, testId, isLoading, params, meta, breadcrumbs, setPageNumber, onPrevPage, onNextPage, paginationMode = PaginationMode.NUMBERED, listingParams, trackMapCardExperimentView, trackMapCardClick, showMapCardExperimentView, onListingCardClick, onListingCardClickWithCompliance, isOnSidePanel, useListingCardWithCompliance, useListingCardV3 = false, favouriteSource, onListingHover }: ListingsGridProps) {
  const { isMobile } = useFeaturesContext();
  const router = useRouter();
  const isOnMilitaryBaseAreaPage = !!parseMilitaryPath(router.asPath);
  const isOnAreaPage = router.asPath.includes('-real-estate') || isOnMilitaryBaseAreaPage;
  const imagePreloadAmount = isMobile ? 2 : 9;
  const [isLazyListingsVisible, setIsLazyListingsVisible] = useState(false);
  const [isPaginationNavVisible, setIsPaginationNavVisible] = useState(false);
  const [isBreadcrumbsVisible, setIsBreadcrumbsVisible] = useState(false);

  const analytics = useMemo(() => ({
    favouriteSource: favouriteSource ?? (isOnSidePanel ? PROPERTY_FAVOURITED_SOURCE_MAP_GRID : PROPERTY_FAVOURITED_SOURCE_AREA_PAGE),
  }), [favouriteSource, isOnSidePanel]);

  // V2 click handler for ListingCard v2 - use passed handler with analytics or fallback to default
  const defaultOnListingCardClickV2 = useListingCardClick();
  const onListingCardClickV2 = onListingCardClickWithCompliance || defaultOnListingCardClickV2;

  // Reactively compute compliance for v2 listings
  const listingsWithReactiveCompliance = useListingsCompliance(listingsWithCompliance);

  const shouldUseV2 = useListingCardWithCompliance && listingsWithCompliance && listingsWithCompliance.length > 0;
  const activeListings = shouldUseV2 ? listingsWithReactiveCompliance : listings;
  const effectiveFavouriteSource = favouriteSource ?? (isOnSidePanel ? PROPERTY_FAVOURITED_SOURCE_MAP_GRID : PROPERTY_FAVOURITED_SOURCE_AREA_PAGE);

  useEffect(() => {
    if (trackMapCardExperimentView) trackMapCardExperimentView();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LoadingDataSimple isLoading={isLoading} data={activeListings}>
      {listings =>
        <>
          <div className={isOnSidePanel ? styles['side-panel-grid'] : styles.grid} data-testid={testId}>
            {listings.map((listing, index) =>{
              const isPreloaded = index < imagePreloadAmount;

              const wrapListingHover = (node: React.ReactNode, key: number): React.ReactNode => {
                if (!onListingHover) {
                  return <Fragment key={key}>{node}</Fragment>;
                }
                return (
                  <div
                    key={key}
                    role="presentation"
                    onMouseEnter={() => onListingHover(listing.id)}
                    onMouseLeave={() => onListingHover(null)}
                  >
                    {node}
                  </div>
                );
              };

              // Render the appropriate card component based on v2 / v3 flags
              const renderCard = (usePriority: boolean) => {
                if (useListingCardV3) {
                  return <ListingCardV3
                    listing={listing}
                    key={`listings-grid-card-${listing.id}`}
                    isImageVisible={usePriority}
                    onListingCardClick={onListingCardClick}
                    isOnMapSidePanel={isOnSidePanel}
                    favouriteSource={effectiveFavouriteSource}
                    activeSmartTagNames={listingParams?.filter.smartTagNames}
                    showDefaultSmartTag
                  />;
                }
                if (shouldUseV2) {
                  return <ListingCardV2
                    listing={listing}
                    key={`listings-grid-card-${listing.id}`}
                    usePriorityMode={usePriority}
                    onListingCardClick={onListingCardClickV2}
                    compliance={listing.compliance}
                    analytics={analytics}
                  />;
                }
                return <ListingCard
                  listing={listing}
                  key={`listings-grid-card-${listing.id}`}
                  isImageVisible={usePriority}
                  onListingCardClick={onListingCardClick}
                  isOnMapSidePanel={isOnSidePanel}
                  favouriteSource={effectiveFavouriteSource}
                />;
              };

              if (index === 8 && isOnAreaPage && listingParams) {
                const listingCardNode = shouldUseV2 ? (
                  <LoadWhenVisible onValueChange={() => setIsLazyListingsVisible(true)}>
                    {isLazyListingsVisible ? renderCard(false) : <LoadingSkeleton />}
                  </LoadWhenVisible>
                ) : (
                  <ConditionalWrapper condition={isOnAreaPage} wrapper={children => <div onClick={() => trackEvent(GTM_CLICK_LISTING_CARD)}>{children}</div>}>
                    <LoadWhenVisible onValueChange={() => setIsLazyListingsVisible(true)}>
                      {isLazyListingsVisible ? renderCard(false) : <LoadingSkeleton />}
                    </LoadWhenVisible>
                  </ConditionalWrapper>
                );
                return (
                  <Fragment key={index}>
                    <SaveSearchCard listingParams={listingParams} isCompact={isOnSidePanel}/>
                    {wrapListingHover(listingCardNode, index)}
                  </Fragment>
                );
              }
              if (index === 3 && isOnAreaPage && showMapCardExperimentView) {
                return wrapListingHover(
                  <>
                    <MapButtonCard trackMapCardClick={trackMapCardClick}/>
                    {/* Only wrap with tracking div for legacy cards, v2 handles tracking internally */}
                    {shouldUseV2 ? (
                      <LoadWhenVisible onValueChange={() => setIsLazyListingsVisible(true)}>
                        {isLazyListingsVisible ? renderCard(false) : <LoadingSkeleton />}
                      </LoadWhenVisible>
                    ) : (
                      <ConditionalWrapper condition={isOnAreaPage} wrapper={children => <div onClick={() => trackEvent(GTM_CLICK_LISTING_CARD)}>{children}</div>}>
                        <LoadWhenVisible onValueChange={() => setIsLazyListingsVisible(true)}>
                          {isLazyListingsVisible ? renderCard(false) : <LoadingSkeleton />}
                        </LoadWhenVisible>
                      </ConditionalWrapper>
                    )}
                  </>,
                  index
                );
              }

              // Regular listing card rendering
              const cardElement = (
                <>
                  {isPreloaded && renderCard(index === 0)}
                  {!isPreloaded && (
                    <LoadWhenVisible onValueChange={() => setIsLazyListingsVisible(true)}>
                      {isLazyListingsVisible ? renderCard(false) : <LoadingSkeleton />}
                    </LoadWhenVisible>
                  )}
                </>
              );

              // Only wrap with tracking div for legacy cards, v2 handles tracking internally
              const row = shouldUseV2 ? (
                <Fragment>{cardElement}</Fragment>
              ) : (
                <ConditionalWrapper condition={isOnAreaPage} wrapper={children => <div onClick={() => trackEvent(GTM_CLICK_LISTING_CARD)}>{children}</div>}>
                  {cardElement}
                </ConditionalWrapper>
              );
              return wrapListingHover(row, index);
            })}
          </div>
          <LoadWhenVisible onValueChange={() => setIsPaginationNavVisible(true)}>
            {isPaginationNavVisible && paginationMode === PaginationMode.PREVNEXT && (
              <PaginationNav
                mode={PaginationMode.PREVNEXT}
                pageNumber={params.page.number}
                totalPages={meta.totalPages}
                onPrev={onPrevPage}
                onNext={onNextPage}
              />
            )
            }
            {isPaginationNavVisible && paginationMode === PaginationMode.NUMBERED && (
              <PaginationNav
                pageNumber={params.page.number}
                totalPages={meta.totalPages}
                setPageNumber={setPageNumber}
              />
            )}
            {!isPaginationNavVisible && <LoadingSkeleton />}
          </LoadWhenVisible>
          <LoadWhenVisible onValueChange={() => setIsBreadcrumbsVisible(true)}>
            {isBreadcrumbsVisible && <ListingsGridBreadcrumbs breadcrumbs={breadcrumbs} />}
            {!isBreadcrumbsVisible && <LoadingSkeleton />}
          </LoadWhenVisible>
        </>
      }
    </LoadingDataSimple>
  );
}
